import React, { useState, useEffect } from 'react';
import { 
  BatteryFull, 
  BatteryMedium, 
  BatteryLow, 
  ChevronRight,
  Search,
  CpuIcon
} from 'lucide-react';

import Sidebar from './sidebar';

const DeskManagement = () => {
  const [desks, setDesks] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingDesk, setEditingDesk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDesk, setExpandedDesk] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sectionExpanded, setSectionExpanded] = useState(true); // Expanded by default

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  
  // Function to compare desks for sorting
  const compareDesks = (a, b) => {
    // Extract numbers from desk IDs
    const getNumericPart = (id) => {
      const matches = id.match(/\d+/);
      return matches ? parseInt(matches[0], 10) : 0;
    };
    
    return getNumericPart(a.id) - getNumericPart(b.id);
  };
  
  // Helper function to safely parse dates
  const safelyParseDate = (dateString) => {
    if (!dateString || dateString.trim() === '') return null;
    
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  };
  
  // Format date for form input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    const date = safelyParseDate(dateString);
    if (!date) return '';
    
    return date.toISOString().split('T')[0];
  };
  
  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = safelyParseDate(dateString);
    if (!date) return 'N/A';
    
    return date.toLocaleDateString();
  };
  
  // Fetch desks from API
  const fetchDesks = async () => {
    try {
      setLoading(true);
      
      const response = await fetch('https://njs-01.optimuslab.space/api/desks');
      
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Transform API data to match our component needs
      const transformedData = data.map(desk => {
        // Safely parse dates
        const startDate = desk.assignee_start_date ? formatDateForInput(desk.assignee_start_date) : '';
        const lastActive = safelyParseDate(desk.quarter_timestamp);
        
        return {
          id: desk.desk_id || '',
          sensorId: desk.sensor_id || '',
          assigneeName: desk.assignee_name || '',
          assigneeEmail: desk.assignee_email || '',
          team: desk.assignee_team || 'Unassigned',
          cardNumber: desk.assignee_card_number || '',
          startDate: startDate,
          battery: desk.battery || 0,
          lastActive: lastActive,
          lastUpdated: lastActive ? lastActive.toLocaleString() : 'Never',
        };
      }).sort(compareDesks);
      
      setDesks(transformedData);
      setError(null);
    } catch (err) {
      console.error('Error fetching desks:', err);
      setError('Failed to load desks. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Load desks on component mount
  useEffect(() => {
    fetchDesks();
  }, []);
  
  const handleEdit = (desk) => {
    setEditingDesk({ ...desk });
    setIsEditModalOpen(true);
  };

  const handleSave = async () => {
    const success = await updateDesk(editingDesk.id, editingDesk);
    
    if (success) {
      setIsEditModalOpen(false);
      // Refresh the desks list to show updated data
      fetchDesks();
    } else {
      alert('Failed to update desk. Please try again.');
    }
  };

  const updateDesk = async (deskId, updatedData) => {
    try {
      // Find the current desk data to preserve unchanged fields
      const currentDesk = desks.find(desk => desk.id === deskId);
      
      if (!currentDesk) {
        throw new Error("Desk not found");
      }
      
      // Format the start date for the API (using ISO format with time)
      let formattedStartDate = null;
      if (updatedData.startDate) {
        const startDate = new Date(updatedData.startDate);
        if (!isNaN(startDate.getTime())) {
          formattedStartDate = startDate.toISOString();
        }
      }
  
      // Format lastActive date if it exists
      let formattedLastActive = null;
      if (currentDesk.lastActive && !isNaN(currentDesk.lastActive.getTime())) {
        formattedLastActive = currentDesk.lastActive.toISOString();
      }
  
      // Prepare the request body with the updated startDate
      const requestBody = {
        desk_id: deskId,
        sensor_id: currentDesk.sensorId,
        assignee_name: updatedData.assigneeName,
        assignee_email: updatedData.assigneeEmail,
        assignee_team: updatedData.team,
        assignee_card_number: updatedData.cardNumber,
        assignee_start_date: formattedStartDate,
        battery: currentDesk.battery,
        quarter_timestamp: formattedLastActive
      };
  
      // Log the request body for debugging
      console.log("Request Body for Desk Update:", requestBody);
  
      // Create the API request
      const response = await fetch(`https://njs-01.optimuslab.space/api/desks/${deskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("API Error Response:", errorData);
        throw new Error(`API responded with status: ${response.status}`);
      }
      
      return true;
    } catch (err) {
      console.error('Error updating desk:', err);
      return false;
    }
  };

  // Helper function to get the appropriate battery icon
  const getBatteryIcon = (batteryLevel) => {
    if (batteryLevel > 70) {
      return <BatteryFull className="text-green-500 w-5 h-5" />;
    } else if (batteryLevel >= 40) {
      return <BatteryMedium className="text-yellow-500 w-5 h-5" />;
    } else {
      return <BatteryLow className="text-red-500 w-5 h-5" />;
    }
  };

  // Search desks by ID, assignee name, email, or team
  const filteredDesks = desks.filter(desk => {
    if (!searchTerm) return true;
    
    const lowercaseSearch = searchTerm.toLowerCase();
    const deskId = (desk.id || '').toLowerCase();
    const assigneeName = (desk.assigneeName || '').toLowerCase();
    const assigneeEmail = (desk.assigneeEmail || '').toLowerCase();
    const team = (desk.team || '').toLowerCase();
    
    return deskId.includes(lowercaseSearch) || 
           assigneeName.includes(lowercaseSearch) ||
           assigneeEmail.includes(lowercaseSearch) ||
           team.includes(lowercaseSearch);
  });

  // Toggle desk details
  const toggleDeskDetails = (deskId) => {
    if (expandedDesk === deskId) {
      setExpandedDesk(null);
    } else {
      setExpandedDesk(deskId);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <div className="bg-white p-6 shadow flex items-center relative">
        {/* Hamburger Menu */}
        <button 
          onClick={toggleSidebar}
          className="text-gray-600 hover:text-gray-900 focus:outline-none z-10"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        
        {/* Centered Logo */}
        <div className="absolute left-0 right-0 mx-auto flex justify-center">
          <img 
            src="/download.png" 
            alt="CUHK INNOPORT" 
            className="h-12" 
          />
        </div>
      </div>

      <div className="my-6 mx-8">
        <div className="flex justify-between items-center md:mb-6 mb-4">
          <h1 className="md:text-[28px] text-xl font-medium">Desks</h1>
        </div>
        
        {/* Search Bar */}
        <div className="relative mb-6">
          <div className="flex items-center border rounded-md bg-white shadow-sm">
            <div className="px-3 py-2">
              <Search className="w-5 h-5 text-gray-500" />
            </div>
            <input
              type="text"
              placeholder="Search by Desk ID, Assignee, or Team"
              value={searchTerm}
              onChange={handleSearchChange}
              className="flex-1 p-2 outline-none"
            />
            {searchTerm && (
              <button 
                onClick={clearSearch}
                className="px-3 py-2 text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            )}
          </div>
        </div>
        
        {/* Loading and Error States */}
        {loading && <div className="text-center py-8">Loading desks...</div>}
        {error && <div className="text-center py-8 text-red-500">{error}</div>}
        
        {/* No Results Message */}
        {!loading && !error && filteredDesks.length === 0 && searchTerm && (
          <div className="text-center py-8 text-gray-500">
            No desks found matching "{searchTerm}"
          </div>
        )}
        
        {/* Single Room Section - Always expanded by default */}
        {!loading && !error && filteredDesks.length > 0 && (
          <div className="mb-6">
            <div 
              className="bg-white rounded-lg border border-[#E2E2E4] shadow-[0_1px_2px_0_#dedede] overflow-hidden"
            >
              <div 
                className="flex items-center justify-between p-4 cursor-pointer"
                onClick={() => setSectionExpanded(!sectionExpanded)}
              >
                <div className="flex items-center">
                  <div className="bg-gray-100 rounded-full p-3 mr-4">
                    <CpuIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">Room 2A Occupancy Sensors</h2>
                    <p className="text-sm text-gray-500">{filteredDesks.length} desks</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <ChevronRight className={`w-6 h-6 transform transition-transform ${sectionExpanded ? 'rotate-90' : ''}`} />
                </div>
              </div>
              
              {/* Desks List - Expanded by default */}
              {sectionExpanded && (
                <div className="border-t">
                  {filteredDesks.map(desk => (
                    <div key={desk.id} className="border-b last:border-b-0">
                      <div 
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleDeskDetails(desk.id)}
                      >
                        <div className="flex items-center space-x-3">
                          <div>
                            <h3 className="font-medium sm:text-lg text-sm">{desk.id}</h3>
                            <p className="text-sm text-gray-500">{desk.assigneeName || 'Unassigned'}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-8">
                          <div className="flex items-center">
                            {getBatteryIcon(desk.battery)}
                            <span className="ml-1 text-sm">{desk.battery}%</span>
                          </div>
                          <ChevronRight className={`w-5 h-5 transform transition-transform ${expandedDesk === desk.id ? 'rotate-90' : ''}`} />
                        </div>
                      </div>
                      
                      {/* Expanded Desk Details */}
                      {expandedDesk === desk.id && (
                        <div className="p-4 bg-gray-50 border-t">
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <p className="text-sm text-gray-500">Sensor ID</p>
                              <p>{desk.sensorId}</p>
                            </div>
                            
                            <div>
                              <p className="text-sm text-gray-500">Assignee</p>
                              <p>{desk.assigneeName || 'Unassigned'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Email</p>
                              <p>{desk.assigneeEmail || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Team</p>
                              <p>{desk.team || 'Unassigned'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Card Number</p>
                              <p>{desk.cardNumber || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Start Date</p>
                              <p>{formatDisplayDate(desk.startDate)}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Battery</p>
                              <div className="flex items-center">
                                {getBatteryIcon(desk.battery)}
                                <span className="ml-1">{desk.battery}%</span>
                              </div>
                            </div>
                            <div className="col-span-2">
                              <p className="text-sm text-gray-500">Last Updated</p>
                              <p>{desk.lastUpdated}</p>
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(desk);
                              }}
                              className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Edit Modal */}
        {isEditModalOpen && editingDesk && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Edit Desk</h2>
                <button
                  onClick={() => setIsEditModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Desk ID
                  </label>
                  <input
                    type="text"
                    value={editingDesk?.id || ''}
                    readOnly
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assignee Name
                  </label>
                  <input
                    type="text"
                    value={editingDesk?.assigneeName || ''}
                    onChange={(e) => setEditingDesk({
                      ...editingDesk,
                      assigneeName: e.target.value
                    })}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assignee Email
                  </label>
                  <input
                    type="email"
                    value={editingDesk?.assigneeEmail || ''}
                    onChange={(e) => setEditingDesk({
                      ...editingDesk,
                      assigneeEmail: e.target.value
                    })}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team
                  </label>
                  <input
                    type="text"
                    value={editingDesk?.team || ''}
                    onChange={(e) => setEditingDesk({
                      ...editingDesk,
                      team: e.target.value
                    })}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Card Number
                  </label>
                  <input
                    type="text"
                    value={editingDesk?.cardNumber || ''}
                    onChange={(e) => setEditingDesk({
                      ...editingDesk,
                      cardNumber: e.target.value
                    })}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* New Start Date Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={editingDesk?.startDate || ''}
                    onChange={(e) => setEditingDesk({
                      ...editingDesk,
                      startDate: e.target.value
                    })}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeskManagement;